export enum RbacRole {
  SUPER_ADMIN = 'super-admin',
  DISTRICT_ADMIN = 'district-admin',
  SCHOOL_ADMIN = 'school-admin',
  CASE_MANAGER = 'case-manager',
  TEACHER = 'teacher',
  PROVIDER = 'provider',
  EVALUATOR = 'evaluator',
  COUNSELOR = 'counselor',
  STAFF = 'staff',
  READ_ONLY = 'read-only',
  PARENT = 'parent',
  STUDENT = 'student',
}

export enum Permission {
  STUDENT_READ = 'student:read',
  STUDENT_WRITE = 'student:write',
  STUDENT_CREATE = 'student:create',
  STUDENT_DELETE = 'student:delete',
  DOCUMENT_READ = 'document:read',
  DOCUMENT_WRITE = 'document:write',
  DOCUMENT_SIGN = 'document:sign',
  AUDIT_READ = 'audit:read',
  AUDIT_WRITE = 'audit:write',
  EXPORT_DATA = 'export:data',
  EXPORT_PII = 'export:pii',
  BACKUP_CREATE = 'backup:create',
  BACKUP_DOWNLOAD = 'backup:download',
  SYNC_TRIGGER = 'sync:trigger',
  FORM_DISPATCH = 'form:dispatch',
  ROSTER_SYNC = 'roster:sync',
  WEBHOOK_MANAGE = 'webhook:manage',
  SSO_CONFIGURE = 'sso:configure',
  USER_MANAGE = 'user:manage',
  ROLE_MANAGE = 'role:manage',
  SYSTEM_CONFIG = 'system:config',
}

export type CanAction = `${string}:${string}` | `${string}:${string}:${string}`

/**
 * WITHIN-TENANT READ SCOPE (TASK-00071) — the FERPA breadth dimension.
 *
 * Tenant isolation (TASK-00029) bounds a principal to their district. It says
 * NOTHING about which children INSIDE that district they may see — and that is
 * where FERPA actually lives: a teacher opening a child who is not theirs is a
 * disclosure. This dimension classifies how far a role's student reads reach:
 *
 *   - `district` — may read any student in their district (the tenant predicate
 *     already bounds them to it; no further school predicate is applied).
 *   - `school`   — may read only students in one of THEIR school membership(s).
 *   - `self`     — may read only their own linked record(s); an arbitrary
 *     student read cannot be verified as their own, so it is denied.
 *
 * Classification rule (Rule 2 — fail closed): when a role's reach was not
 * obvious, it was assigned the TIGHTER scope on purpose. Over-restricting a
 * legitimate reader is a support ticket; under-restricting is a FERPA leak.
 * The rationale for each row is recorded inline in ROLE_PERMISSIONS_MAP.
 */
export type RoleScope = 'district' | 'school' | 'self'

export interface RolePermissions {
  role: RbacRole
  permissions: Permission[]
  canManageRoles: boolean
  maxCaseload: number
  /** How far this role's student reads reach within its tenant. See RoleScope. */
  scope: RoleScope
}

export const ROLE_PERMISSIONS_MAP: Record<RbacRole, RolePermissions> = {
  [RbacRole.SUPER_ADMIN]: {
    role: RbacRole.SUPER_ADMIN, permissions: Object.values(Permission),
    canManageRoles: true, maxCaseload: 0,
    // Platform/tenant operator with the full permission set; district-wide by
    // definition within any tenant they operate in.
    scope: 'district',
  },
  [RbacRole.DISTRICT_ADMIN]: {
    role: RbacRole.DISTRICT_ADMIN,
    permissions: [
      Permission.STUDENT_READ, Permission.STUDENT_WRITE, Permission.STUDENT_CREATE,
      Permission.DOCUMENT_READ, Permission.DOCUMENT_WRITE, Permission.DOCUMENT_SIGN,
      Permission.AUDIT_READ, Permission.EXPORT_DATA, Permission.EXPORT_PII,
      Permission.BACKUP_CREATE, Permission.BACKUP_DOWNLOAD, Permission.SYNC_TRIGGER,
      Permission.FORM_DISPATCH, Permission.ROSTER_SYNC, Permission.WEBHOOK_MANAGE,
      Permission.USER_MANAGE, Permission.ROLE_MANAGE, Permission.SYSTEM_CONFIG,
    ],
    canManageRoles: true, maxCaseload: 0,
    // Administers the whole district; reads across every school in it.
    scope: 'district',
  },
  [RbacRole.SCHOOL_ADMIN]: {
    role: RbacRole.SCHOOL_ADMIN,
    permissions: [
      Permission.STUDENT_READ, Permission.STUDENT_WRITE, Permission.STUDENT_CREATE,
      Permission.DOCUMENT_READ, Permission.DOCUMENT_WRITE, Permission.DOCUMENT_SIGN,
      Permission.AUDIT_READ, Permission.EXPORT_DATA, Permission.FORM_DISPATCH,
      Permission.USER_MANAGE,
    ],
    canManageRoles: true, maxCaseload: 0,
    // Administers a single school; reads are bounded to their school(s).
    scope: 'school',
  },
  [RbacRole.CASE_MANAGER]: {
    role: RbacRole.CASE_MANAGER,
    permissions: [
      Permission.STUDENT_READ, Permission.STUDENT_WRITE, Permission.STUDENT_CREATE,
      Permission.DOCUMENT_READ, Permission.DOCUMENT_WRITE, Permission.DOCUMENT_SIGN,
      Permission.AUDIT_READ, Permission.EXPORT_DATA, Permission.FORM_DISPATCH,
    ],
    canManageRoles: false, maxCaseload: 40,
    // Manages an IEP caseload within a school. The finer "only your caseload"
    // predicate is deferred (needs an assignment table — see the caseload task);
    // school is the tighter, safe classification until then.
    scope: 'school',
  },
  [RbacRole.TEACHER]: {
    role: RbacRole.TEACHER,
    permissions: [
      Permission.STUDENT_READ, Permission.DOCUMENT_READ, Permission.DOCUMENT_SIGN,
    ],
    canManageRoles: false, maxCaseload: 150,
    // Teaches at a school; reads are bounded to their school(s).
    scope: 'school',
  },
  [RbacRole.PROVIDER]: {
    role: RbacRole.PROVIDER,
    permissions: [
      Permission.STUDENT_READ, Permission.DOCUMENT_READ, Permission.DOCUMENT_WRITE,
    ],
    canManageRoles: false, maxCaseload: 0,
    // Related-service provider assigned to school(s). May travel between schools
    // in a district, but the membership carries the schools they serve — tighter
    // (school) until a district-provider role is modeled explicitly.
    scope: 'school',
  },
  [RbacRole.EVALUATOR]: {
    role: RbacRole.EVALUATOR,
    permissions: [
      Permission.STUDENT_READ, Permission.DOCUMENT_READ, Permission.DOCUMENT_WRITE,
    ],
    canManageRoles: false, maxCaseload: 0,
    // School psychologist / evaluator assigned to school(s); tighter (school)
    // until a district-evaluator role is modeled explicitly.
    scope: 'school',
  },
  [RbacRole.COUNSELOR]: {
    role: RbacRole.COUNSELOR,
    permissions: [
      Permission.STUDENT_READ, Permission.DOCUMENT_READ, Permission.DOCUMENT_WRITE,
    ],
    canManageRoles: false, maxCaseload: 0,
    // Counsels at a school; reads are bounded to their school(s).
    scope: 'school',
  },
  [RbacRole.STAFF]: {
    role: RbacRole.STAFF,
    permissions: [Permission.STUDENT_READ, Permission.DOCUMENT_READ],
    canManageRoles: false, maxCaseload: 0,
    // Generic staff — least privilege; unsure -> tighter (school).
    scope: 'school',
  },
  [RbacRole.READ_ONLY]: {
    role: RbacRole.READ_ONLY,
    permissions: [Permission.STUDENT_READ, Permission.DOCUMENT_READ],
    canManageRoles: false, maxCaseload: 0,
    // Ambiguous (district auditor vs school aide); unsure -> tighter (school).
    scope: 'school',
  },
  [RbacRole.PARENT]: {
    role: RbacRole.PARENT,
    permissions: [Permission.DOCUMENT_SIGN],
    canManageRoles: false, maxCaseload: 0,
    // May see only their own child — an individual (self) scope. The
    // guardian->student link is a separate control; an arbitrary student read
    // by a parent is denied here.
    scope: 'self',
  },
  [RbacRole.STUDENT]: {
    role: RbacRole.STUDENT,
    permissions: [],
    canManageRoles: false, maxCaseload: 0,
    // May see only their own record — an individual (self) scope.
    scope: 'self',
  },
}

/**
 * Resolve a role id to its within-tenant read scope, FAIL CLOSED (Rule 2): an
 * unknown or unclassified role is the TIGHTEST scope ('self'), which denies any
 * read of a record that cannot be proven to be the caller's own. The scope for
 * every canonical role is the single source of truth in ROLE_PERMISSIONS_MAP
 * above (Rule 3 — derive, don't enumerate a second list).
 */
export function resolveRoleScope(roleId: string): RoleScope {
  const entry = (ROLE_PERMISSIONS_MAP as Record<string, RolePermissions | undefined>)[roleId]
  return entry?.scope ?? 'self'
}

export function hasPermission(role: RbacRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS_MAP[role]?.permissions.includes(permission) ?? false
}

export function can(permissions: Set<string>, required: string): boolean {
  if (permissions.has('*:*:*') || permissions.has('*:*')) return true
  if (permissions.has(required)) return true
  const [domain, action] = required.split(':')
  if (permissions.has(`${domain}:*`) || permissions.has(`${domain}:*:*`)) return true
  if (permissions.has(`*:${action}`)) return true
  return false
}
